"use client";

import { LocaleLink, useTranslator } from "~/i18n/provider";
import { paginationItems, setPageParam } from "~/lib/storefront/page-params";
import { cn } from "~/lib/utils";

/**
 * P2-13 · Numbered pagination. A labelled `<nav>` of real crawlable `<a href>`
 * links (SEO — P2-DB02 §01) that preserve every filter/sort param and drop
 * `page=1` for canonical URLs. Server-rendered; the current page carries
 * `aria-current`. Renders nothing for a single page.
 */
export function Pagination({
  currentPage,
  totalPages,
  path,
  search,
}: {
  currentPage: number;
  totalPages: number;
  /** The PLP pathname, e.g. `/c/jackets`. */
  path: string;
  /** The current query string (filters + sort + view); page is rewritten per link. */
  search: string;
}) {
  const t = useTranslator();
  if (totalPages <= 1) return null;

  const href = (page: number) => {
    const qs = setPageParam(search, page);
    return qs ? `${path}?${qs}` : path;
  };
  const items = paginationItems(currentPage, totalPages);

  return (
    <nav
      aria-label="Pagination"
      className="flex items-center justify-center gap-1 pt-8"
    >
      <PageLink
        href={href(currentPage - 1)}
        disabled={currentPage <= 1}
        label={t("browse.previousPage")}
      >
        ‹
      </PageLink>

      {items.map((item, i) =>
        item === "ellipsis" ? (
          <span
            key={`e${i}`}
            aria-hidden="true"
            className="px-2 text-sm text-fg-muted"
          >
            …
          </span>
        ) : (
          <LocaleLink
            key={item}
            href={href(item)}
            aria-label={`Page ${item}`}
            aria-current={item === currentPage ? "page" : undefined}
            className={cn(
              "flex h-9 min-w-9 items-center justify-center rounded-md border px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
              item === currentPage
                ? "border-primary bg-primary text-primary-fg"
                : "border-border text-fg hover:bg-surface-2",
            )}
          >
            {item}
          </LocaleLink>
        ),
      )}

      <PageLink
        href={href(currentPage + 1)}
        disabled={currentPage >= totalPages}
        label={t("browse.nextPage")}
      >
        ›
      </PageLink>
    </nav>
  );
}

function PageLink({
  href,
  disabled,
  label,
  children,
}: {
  href: string;
  disabled: boolean;
  label: string;
  children: React.ReactNode;
}) {
  const className =
    "flex h-9 min-w-9 items-center justify-center rounded-md border border-border px-2 text-sm";
  if (disabled) {
    return (
      <span
        aria-disabled="true"
        className={cn(className, "text-fg-muted opacity-40")}
      >
        {children}
      </span>
    );
  }
  return (
    <LocaleLink
      href={href}
      aria-label={label}
      className={cn(
        className,
        "text-fg hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
      )}
    >
      {children}
    </LocaleLink>
  );
}
